/*
 * KI CART LADDER
 *
 * Adds and removes the free gift for the two-tier cart ladder.
 *
 * Loaded from layout/theme.liquid, NOT from the cart snippet. The cart is
 * re-rendered by assigning innerHTML, and scripts inserted that way never
 * execute — so all handlers here are delegated from `document` and survive
 * every re-render.
 *
 * The removal half matters more than the add half. If a shopper qualifies,
 * takes the gift, then removes items and drops back under the threshold, the
 * gift must come out. Otherwise they are silently charged $69.95 for something
 * the interface told them was free.
 */
(function () {
  'use strict';

  var GIFT_PROPERTY = '_ki_gift';
  var CART_CONTENT = '.js-ajax-cart-content';
  var busy = false;

  function ajaxCartUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + '/cart?view=ajax';
  }

  /* Mirrors the theme's own refresh: fetch the ajax cart template, pull the
     [data-html="content"] block out of it, swap it into every cart container. */
  function refreshCart() {
    return fetch(ajaxCartUrl(), { credentials: 'same-origin' })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var content = holder.querySelector('[data-html="content"]');
        if (!content) return;
        document.querySelectorAll(CART_CONTENT).forEach(function (node) {
          node.innerHTML = content.innerHTML;
        });
        document.dispatchEvent(new CustomEvent('ki:cart:rendered'));
      });
  }

  function addGift(variantId, button) {
    if (busy) return;
    busy = true;
    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
    }

    var properties = {};
    properties[GIFT_PROPERTY] = 'true';

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, properties: properties })
    })
      .then(function (res) {
        if (!res.ok) throw res;
        return refreshCart();
      })
      .catch(function () {
        /* Most likely cause is the colourway selling out. Say so plainly
           rather than leaving a dead button. */
        var ladder = document.querySelector('[data-ki-ladder]');
        if (ladder) {
          var msg = document.createElement('p');
          msg.className = 'ki-ladder__error mb0';
          msg.textContent = 'That colour is unavailable. Try another.';
          ladder.appendChild(msg);
        }
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');
        }
      })
      .then(function () { busy = false; });
  }

  function removeGift(line) {
    if (busy) return;
    busy = true;

    fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: 0 })
    })
      .then(function (res) {
        if (!res.ok) throw res;
        return refreshCart();
      })
      .catch(function () { /* leave the cart as-is rather than thrash */ })
      .then(function () { busy = false; });
  }

  /* Slide the cart drawer out.
   *
   * Every custom add-to-cart on this store posts to /cart/add.js directly and
   * never reaches Icon's own handler, so none of them would give the shopper
   * any confirmation. This is the one place that knows how to open the drawer,
   * exposed on window so ki-belt and ki-loadout can call it too.
   *
   * Two routes, most-specific first. showDrawer() is the theme's own entry
   * point and does its own bookkeeping; WAU.Slideout is what showDrawer calls
   * underneath and is the fallback if the theme's cart object has not
   * initialised. Returns false if neither exists, so callers can decide
   * whether to fall back to their own inline confirmation.
   */
  var DRAWER_ID = 'slideout-ajax-cart';

  function drawerEl() { return document.getElementById(DRAWER_ID); }

  /* The drawer is a native popover on this theme (popover="auto"), so
     :popover-open is the honest test. Fall back to display for older engines. */
  function drawerIsOpen() {
    var el = drawerEl();
    if (!el) return false;
    try { if (el.matches(':popover-open')) return true; } catch (e) { /* older engine */ }
    return getComputedStyle(el).display !== 'none';
  }

  /* The theme parks a spinner over the drawer and only ever clears it inside
     ajaxCart.showDrawer(). Open the drawer any other way and that spinner sits
     there permanently over dimmed content, which looks more broken than not
     opening at all. We refresh the cart BEFORE opening, so by the time the
     drawer appears the content is already current and the loader can go
     immediately rather than after the theme's 800ms delay. */
  function hideDrawerLoader() {
    var loader = document.querySelector('.ajax-cart__drawer-wrapper .js-mini-cart-loader');
    if (!loader) return;
    try {
      if (window.theme && window.theme.Helpers && window.theme.Helpers.fadeOut) {
        window.theme.Helpers.fadeOut(loader);
        return;
      }
    } catch (e) { /* fall through to the blunt version */ }
    loader.style.display = 'none';
  }

  function openCartDrawer() {
    if (!drawerEl()) return false;
    if (drawerIsOpen()) { hideDrawerLoader(); return true; }

    /* CHECK AFTER EVERY ATTEMPT, never assume.
       The first version called showDrawer() and returned true because it did
       not throw. showDrawer opens with `if (config.cart_action != 'drawer')
       return false`, and the config passed did not carry that key, so it bailed
       without opening anything — while callers, trusting the true, suppressed
       their own inline confirmation. The shopper got no feedback at all.
       Verified silence is the worst outcome of the three, so every route is now
       tried and then measured. */
    try {
      if (window.WAU && window.WAU.Slideout && window.WAU.Slideout._openByName) {
        window.WAU.Slideout._openByName(DRAWER_ID);
      }
    } catch (e) { /* try the next route */ }

    if (!drawerIsOpen()) {
      try {
        var ajaxCart = window.Shopify && window.Shopify.theme && window.Shopify.theme.ajaxCart;
        if (ajaxCart && typeof ajaxCart.showDrawer === 'function') {
          ajaxCart.showDrawer({ cart_action: 'drawer' });
        }
      } catch (e) { /* try the next route */ }
    }

    if (!drawerIsOpen()) {
      try { drawerEl().showPopover(); } catch (e) { /* out of options */ }
    }

    if (drawerIsOpen()) { hideDrawerLoader(); return true; }
    return false;
  }

  /* Refresh first, then open — otherwise the drawer slides out showing the
     cart as it was BEFORE the item landed, which reads as a failed add. */
  function refreshAndOpen() {
    return refreshCart().then(openCartDrawer);
  }

  window.kiCart = { openDrawer: openCartDrawer, refresh: refreshCart, refreshAndOpen: refreshAndOpen };

  /* Plain add, used by the cart upsell row.
   *
   * Deliberately NOT addGift: that stamps _ki_gift on the line, which marks it
   * as a reward and excludes it from the free-shipping qualifying total. An
   * upsell is a normal purchase and must count toward the threshold, otherwise
   * adding a $24.95 bandage to chase free shipping would move the bar not at
   * all and the customer would rightly think the cart was broken.
   */
  function addItem(variantId, button) {
    if (busy) return;
    busy = true;
    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
    }

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (res) {
        if (!res.ok) throw res;
        return refreshCart();
      })
      .catch(function () {
        /* Re-enable so the shopper can retry. The cart is unchanged. */
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');
          button.textContent = 'Try again';
        }
      })
      .then(function () { busy = false; });
  }

  /* Runs after every cart render. Pulls the gift back out if the cart no
     longer qualifies. Liquid has already excluded the gift from the
     qualifying total, so this cannot oscillate. */
  function enforce() {
    if (busy) return;
    var ladder = document.querySelector('[data-ki-ladder]');
    if (!ladder) return;

    var inCart = ladder.getAttribute('data-ki-gift-in-cart') === 'true';
    var qualified = ladder.getAttribute('data-ki-gift-qualified') === 'true';
    var line = parseInt(ladder.getAttribute('data-ki-gift-line'), 10);

    if (inCart && !qualified && line > 0) removeGift(line);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('.js-ki-gift-add');
    if (!button) return;
    event.preventDefault();
    var variantId = button.getAttribute('data-ki-variant');
    if (variantId) addGift(variantId, button);
  });

  /* Delegated from document, like the gift handler above, because the cart is
     re-rendered by assigning innerHTML and any listener bound to a button
     inside it dies on the next render. */
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('.js-ki-upsell-add');
    if (!button) return;
    event.preventDefault();
    var variantId = button.getAttribute('data-ki-variant');
    if (variantId) addItem(variantId, button);
  });

  /* The theme re-renders the cart on its own for quantity changes and removals,
     so watch the container rather than relying only on our own refreshes. */
  function watch() {
    var targets = document.querySelectorAll(CART_CONTENT);
    if (!targets.length) return;
    var observer = new MutationObserver(function () { enforce(); });
    targets.forEach(function (node) {
      observer.observe(node, { childList: true, subtree: true });
    });
  }

  function init() {
    watch();
    enforce();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('ki:cart:rendered', enforce);
})();
